import styled from '@emotion/styled'

import { TimeReportStatus } from '@bemlo/enums'
import { calculateDisturbedTime } from '@bemlo/schedule'
import { arrayOfAll, isTruthy } from '@bemlo/utils'

import { TimeReport } from './time-report.type'
import { Color, theme } from '../../constants'
import { useTranslations } from '../../translate'
import { Flex } from '../Flex'
import { Icon } from '../Icon'
import { Text } from '../Text'

const isDifferent = (
  report: TimeReport,
  previousReport: TimeReport | undefined,
  key: keyof TimeReport,
) => {
  if (!previousReport) return false
  if (key === 'onCallDisturbances') {
    return (
      calculateDisturbedTime(report.onCallDisturbances, 30) !==
      calculateDisturbedTime(previousReport.onCallDisturbances, 30)
    )
  }

  return report[key] !== previousReport[key]
}

type DataBoxProps = {
  report: TimeReport
  previousReport: TimeReport | undefined
  ml: number
}

const Container = styled(Flex)<{ ml: number }>(({ ml }) => ({
  background: Color.LIGHT_GREY,
  borderRadius: theme.border.radius.semi,
  padding: theme.spacing['2.5'],
  marginLeft: ml,
}))

type Keys =
  | 'reportedStartDateTime'
  | 'reportedEndDateTime'
  | 'reportedBreakTime'
  | 'onCallDisturbances'

const KEYS = arrayOfAll<Keys>()([
  'reportedStartDateTime',
  'reportedEndDateTime',
  'reportedBreakTime',
  'onCallDisturbances',
])

export const DataBox = ({ report, previousReport, ml }: DataBoxProps) => {
  const t = useTranslations()

  const LABELS: Record<Keys, string> = {
    reportedStartDateTime: t.timesheets.start_time(),
    reportedEndDateTime: t.timesheets.end_time(),
    reportedBreakTime: t.timesheets.break_time(),
    onCallDisturbances: t.timesheets.disturbances(),
  }

  const times = KEYS.map((key) => {
    const toLabel = (r: TimeReport) =>
      key === 'reportedBreakTime'
        ? `${r[key]} min`
        : key === 'onCallDisturbances'
          ? t.$n_hours(
              calculateDisturbedTime(r.onCallDisturbances, 30) / 60,
              true,
            )
          : t.$date.unix(r[key]).tz('UTC', true).format('HH:mm')

    const hasChanged = isDifferent(report, previousReport, key)

    const hasNoDisturbances =
      !report.onCallDisturbances.length &&
      !previousReport?.onCallDisturbances.length

    if (key === 'onCallDisturbances' && hasNoDisturbances) return null

    return {
      key,
      hasChanged,
      toLabel,
      hasNoDisturbances,
    }
  }).filter(isTruthy)

  const showTimes =
    report.status === TimeReportStatus.REPORTED ||
    times.some((time) => time.hasChanged)

  if (!showTimes && !report.comment) return null

  return (
    <Container col gap={1.5} ml={ml}>
      {showTimes &&
        times.map(({ key, hasChanged, toLabel }) => {
          return (
            <Flex
              key={key}
              gap={1.5}
              styles={{
                color: Color.CHARCOAL,
                justifyContent: 'space-between',
                fontSize: theme.font.size['6.5'],
              }}
            >
              <Text>{LABELS[key]}</Text>
              <Flex gap={1.5} items="center">
                {hasChanged && previousReport && (
                  <>
                    <Text
                      color={Color.BLUE}
                      styles={{
                        textDecoration: 'line-through',
                        color: Color.DARK_GREY,
                      }}
                    >
                      {toLabel(previousReport)}
                    </Text>
                    <Icon size="sm" name="ArrowRightIcon" />
                  </>
                )}
                <Text
                  fontWeight={600}
                  styles={{ minWidth: 40, textAlign: 'right' }}
                >
                  {toLabel(report)}
                </Text>
              </Flex>
            </Flex>
          )
        })}
      {report.comment && (
        <Text fontSize={theme.font.size[6]} color={Color.DARK_GREY}>
          {report.comment}
        </Text>
      )}
    </Container>
  )
}
